<template>
  <div
    ref="mapContainer"
    :class="{ 'fullscreen-map-container': fullscreen }"
    style="height: 100%;"
  >
    <map-center
      :lat="centerLat"
      :lng="centerLng"
      :zoom="this.map ? this.map.getZoom() : 1"
      :markers="markers"
      :id="'map-center' + mapId"
    />
    <map-toolbar
      :isSidebarVisible="isSidebarVisible"
      :fullscreen="fullscreen"
      :toggleSidebar="toggleSidebar"
      :fullscreenButton="fullscreenButton"
      :toggleFullscreen="toggleFullscreen"
      :map="map"
      ref="mapToolbar"
    />
    <map-sidebar
      :is-visible="isSidebarVisible"
      :toggle-sidebar="toggleSidebar"
      :map-height="mapHeight"
      :map-id="mapId"
    />
    <div
      ref="map"
      :id="mapId"
      class="map"
      :style="{ 'height': mapHeight + 'px' }"
    >
    </div>

  </div>
</template>

<script>
import { LMap, LTileLayer, LControlLayers } from "vue2-leaflet";
// import { GeoSearchControl, OpenStreetMapProvider } from "leaflet-geosearch";
import supercluster from "supercluster";
import axios from "axios";
import leaflet, { marker } from "leaflet";
import leafletDraw from "leaflet-draw";
// import leafletFullscreen from "leaflet-fullscreen";
import nanoid from "nanoid";
import { cachedAxios } from "@/cachedAxios";
import { axiosInstance } from "@/api";
import MapToolbar from "./MapToolbar";
import MapSidebar from "./MapSidebar";
import MapCenter from "./MapCenter";

const statuses = {
  mergingPolylines: "MERGING_POLYLINES",
  cuttingPolyline: "CUTTING_POLYLINE",
  editingPolyline: "EDITING_POLYLINE",
  editingMarker: "EDITING_MARKER"
};

// CalTopo account WMS layer codes (from GetCapabilities on …/wms?service=WMS&request=GetCapabilities)
const CALTOPO_API_WMS_LAYERS = [
  { code: "mbt", name: "MapBuilder Topo" },
  { code: "mbh", name: "MapBuilder Hybrid" },
  // t / mbr: use public XYZ tiles (added below) — WMS "t" is often empty outside USGS scan areas
  { code: "r", name: "Shaded Relief — Normal" },
  { code: "om", name: "OpenStreetMap" },
  { code: "oc", name: "OpenCycleMap" },
  { code: "oo", name: "Thunderforest Outdoors" },
  { code: "mba", name: "MapBuilder Overlay" },
  { code: "sg", name: "Gradient Slope Shading" },

];

// Dev only: webpack devServer proxies this prefix → https://caltopo.com/api (see config/index.js proxyTable).
const CALTOPO_DEV_MAP_PATH_PREFIX = "/caltopo-map-api";

export default {
  data() {
    return {
      map: null,
      mapHeight: "0",
      isSidebarVisible: false,
      fullscreen: false,
      superclusterIndex: null,
      updateClustersLock: false,
      clustersLayer: null,
      didFitBounds: false,
      myCustomEditHandler: null,
      status: false,
      markerOnEdit: null,
      clickLock: 0,
      centerLat: 0,
      centerLng: 0,
      markers: [],
      drawnShapes: [],
      drawnShapesLayer: null,
      polylines: [],
      tileProviders: [],
      mapGroupedLayerControl: null
    };
  },

  computed: {
    mapMarkerTypesList() {
      return this.$store.getters["entityList"]("mapMarkerTypes").sort(
        (a, b) => {
          if (a.order === b.order) {
            return a.title.localeCompare(b.title);
          }
          return b.order - a.order;
        }
      );
    },
    mapMarkerTypes() {
      return this.$store.state.entities.mapMarkerTypes;
    },
    editableDataSourceId() {
      if (this.dataSources) {
        return this.dataSources.findIndex(item => {
          return item.editable;
        });
      }
    },
    dataSources() {
      let dataSourcesGroups = this.$store.getters["maps/dataSourcesGroups"](
        this.mapId
      );
      if (Array.isArray(dataSourcesGroups)) {
        let dataSourcesArray = dataSourcesGroups.map(item => {
          return item.dataSources;
        });
        return dataSourcesArray.flat();
      } else {
        return [];
      }
    },
    activeDataSources() {
      if (Array.isArray(this.dataSources)) {
        return this.dataSources.filter(item => {
          return item.isActive;
        });
      }
      return [];
    },
    isAllEventsMode() {
      const eventIdsToShow = this.$store.getters["events/eventIdsToShow"];
      const showAll = this.$store.getters["events/showAllEvents"];
      return (
        showAll && eventIdsToShow && eventIdsToShow.length > 1
      );
    }
  },

  props: {
    mapId: { type: String, default: null },
    fullscreenButton: { type: Boolean, default: false },
    drawEnabled: { type: Boolean, default: false },
    markerLabelsPermanent: {
      type: Boolean,
      default: true
    }
  },

  methods: {
    bindFeatureNameLabel(layer, label) {
      if (!layer) {
        return;
      }
      if (layer.unbindTooltip) {
        layer.unbindTooltip();
      }
      if (layer.unbindPopup) {
        layer.unbindPopup();
      }
      if (!label || !this.markerLabelsPermanent) {
        return;
      }
      layer.bindTooltip(label, {
        direction: "top",
        className: "drawn-shape-distance-label",
        offset: [0, -12],
        permanent: true
      });
    },
    getEventTitleById(eventId) {
      if (!eventId) {
        return "";
      }
      const id =
        typeof eventId === "object" && eventId._id != null
          ? String(eventId._id)
          : String(eventId);
      const event = this.$store.state.entities.events[id];
      return (event && event.title) || "";
    },
    getEventColor(eventId) {
      // Define a palette of distinct colors for different events
      const eventColors = [
        '#FF6B6B', // Red
        '#4ECDC4', // Teal
        '#45B7D1', // Blue
        '#96CEB4', // Green
        '#FFEAA7', // Yellow
        '#DDA0DD', // Plum
        '#98D8C8', // Mint
        '#F7DC6F', // Gold
        '#BB8FCE', // Lavender
        '#85C1E9', // Light Blue
        '#F8C471', // Orange
        '#82E0AA'  // Light Green
      ];
      
      // Get all event IDs to create a consistent mapping
      const eventIdsToShow = this.$store.getters["events/eventIdsToShow"];
      console.log('Map getEventColor - eventId:', eventId);
      console.log('Map getEventColor - eventIdsToShow:', eventIdsToShow);
      
      if (!eventIdsToShow || !Array.isArray(eventIdsToShow)) {
        console.log('Map getEventColor - no eventIdsToShow, returning default red');
        return '#FF6B6B'; // Default red
      }
      
      // Extract the actual event ID string from the eventId object
      const actualEventId = eventId && eventId._id ? eventId._id : eventId;
      console.log('Map getEventColor - actualEventId:', actualEventId);
      
      // Find the index of the current event in the array
      const eventIndex = eventIdsToShow.indexOf(actualEventId);
      console.log('Map getEventColor - eventIndex:', eventIndex);
      
      if (eventIndex === -1) {
        console.log('Map getEventColor - event not found in array, returning default red');
        return '#FF6B6B'; // Default red if event not found
      }
      
      // Return the color based on the event's position in the array
      // Use modulo to cycle through colors if there are more events than colors
      const selectedColor = eventColors[eventIndex % eventColors.length];
      console.log('Map getEventColor - selectedColor:', selectedColor);
      
      return selectedColor;
    },
    fetchData() {
      let fetchFunctions = [];
      if (!this.dataSources) {
        return;
      }

      for (let dataSource of this.activeDataSources) {
        // Add cache-busting parameter to force fresh data
        let params = Object.assign({}, dataSource.get.params);
        params._t = Date.now(); // Cache-busting timestamp
        
        fetchFunctions.push(
          axiosInstance({
            method: dataSource.get.method,
            url: dataSource.get.url,
            params: params,
            headers: Object.assign({}, dataSource.get.headers)
          })
        );
      }

      Promise.all(fetchFunctions)
        .then(array => {
          this.markers = [];
          this.drawnShapes = [];
          let i = 0;
          for (let { data } of array) {
            let {
              markerType,
              iconColor,
              color,
              glyph
            } = this.activeDataSources[i].defaultProperties;
            let overrideProperties = this.activeDataSources[i]
              .overrideProperties;

            let editable = this.activeDataSources[i].editable;
            let type = this.activeDataSources[i].type || null;
            let geojson = this.activeDataSources[i].get.toGeoJson(data);
            let features = geojson.features.map(item => {
              item.properties.id = item.properties.id || nanoid();

              let overrideGlyph,
                overrideMarkerType,
                overrideIconColor,
                overrideColor;
              if (overrideProperties) {
                overrideGlyph = overrideProperties.glyph;
                overrideMarkerType = overrideProperties.markerType;
                overrideIconColor = overrideProperties.iconColor;
                overrideColor = overrideProperties.color;
              }

              item.properties.glyph = overrideGlyph
                ? overrideGlyph
                : item.properties.glyph || glyph;

              item.properties.markerType = overrideMarkerType
                ? overrideMarkerType
                : item.properties.markerType || markerType;

              item.properties.iconColor = overrideIconColor
                ? overrideIconColor
                : item.properties.iconColor || iconColor;

              item.properties.type = item.properties.type || type;

              // Check if we're in כולם mode and assign different colors per event
              // This must be done AFTER all other property assignments to ensure it doesn't get overridden
              console.log('Map fetchData - processing feature:', {
                eventId: item.properties.eventId,
                isAllEventsMode: this.isAllEventsMode,
                currentColor: item.properties.color,
                overrideColor: overrideColor,
                defaultColor: color
              });
              
              const isDrawnShape =
                item.geometry && this.isDrawableGeometryType(item.geometry.type);

              if (isDrawnShape && item.properties.color) {
                item.properties.fillColor =
                  item.properties.fillColor || item.properties.color;
                console.log('Map fetchData - preserving DRAWN SHAPE color:', item.properties.color);
              } else if (item.properties.eventId && this.isAllEventsMode) {
                const eventColor = this.getEventColor(item.properties.eventId);
                item.properties.color = eventColor;
                console.log('Map fetchData - assigned EVENT color for event:', item.properties.eventId, 'color:', eventColor);
              } else if (overrideColor) {
                item.properties.color = overrideColor;
                console.log('Map fetchData - using OVERRIDE color:', overrideColor);
              } else {
                item.properties.color = color;
                console.log('Map fetchData - using DEFAULT color:', color);
              }
              
              // Final verification
              console.log('Map fetchData - FINAL color assigned:', item.properties.color);

              switch (item.properties.type) {
                case "partner":
                  item.properties.parentData = "";
                default:
                  break;
              }

              item.properties.editable = editable;
              item.meta = { dataSourceId: i };
              return item;
            });
            const pointFeatures = features.filter(
              item => item.geometry && item.geometry.type === "Point"
            );
            const shapeFeatures = features.filter(
              item =>
                item.geometry &&
                (item.geometry.type === "LineString" ||
                  item.geometry.type === "Polygon")
            );
            this.markers = this.markers.concat(pointFeatures);
            this.drawnShapes = this.drawnShapes.concat(shapeFeatures);
            ++i;
          }
        })
        .then(() => {
          this.renderDrawnLayers();
        })
        .catch(err => {
          console.log(err);
        });

      // axios
      //   .get(
      //     "https://cdn.rawgit.com/mapbox/supercluster/v4.0.1/test/fixtures/places.json"
      //   )
      //   .then(({ data }) => {
      //     this.markers = data.features.map(item => {
      //       if (item.properties.id) {
      //         return item;
      //       } else {
      //         item.properties.id = nanoid();
      //         return item;
      //       }
      //     });
      //   });
    },
    fitBounds() {
      if (!this.didFitBounds && this.markers.length) {
        this.didFitBounds = true;

        if (this.markers.length > 1) {
          let bbox = L.latLngBounds(
            this.markers.map(item => {
              let coordinates = item.geometry.coordinates;
              return [coordinates[1], coordinates[0]];
            })
          ).pad(0.5);

          this.map.fitBounds(bbox);
        } else {
          let coordinates = this.markers[0].geometry.coordinates;
          this.map.setView([coordinates[1], coordinates[0]], 10);
        }
      }
    },
    updateClusters(newData = false) {
      if (this.updateClustersLock) {
        return;
      }

      this.updateClustersLock = true;
      let bounds = this.map.getBounds();
      let bbox = [
        bounds.getWest(),
        bounds.getSouth(),
        bounds.getEast(),
        bounds.getNorth()
      ];

      if (this.superclusterIndex) {
        try {
          let zoom = this.map.getZoom();
          let data = this.superclusterIndex.getClusters(bbox, zoom);
          this.clustersLayer.clearLayers();
          this.clustersLayer.addData(data);
        } catch (err) {
          console.log(err);
        }

        if (this.markers.length) {
          bbox = L.latLngBounds(
            this.markers.map(item => {
              let coordinates = item.geometry.coordinates;
              return [coordinates[1], coordinates[0]];
            })
          );
        }
        this.bringMarkersToFront();
        setTimeout(() => {
          this.fitBounds();
        }, 500);
      }

      this.updateClustersLock = false;
    },
    createClusterIcon(feature, latlng) {
      if (!feature.properties.cluster) {
        if (feature.properties.type == "INREACH_DEVICE") {
          const deviceMarker = L.marker(latlng, {
            icon: L.icon({
              iconUrl: "/static/explorer_plus_marker.png",
              iconRetinaUrl: "/static/explorer_plus_marker@2x.png",
              iconSize: [40, 53],
              iconAnchor: [20, 53]
            })
          });
          this.bindFeatureNameLabel(
            deviceMarker,
            this.getFeatureNameLabel(feature)
          );
          return deviceMarker;
        } else {
          let markerTypeIcon;
          if (this.mapMarkerTypes[feature.properties.markerType]) {
            markerTypeIcon = this.mapMarkerTypes[feature.properties.markerType]
              .icon;
          }
          let icon = feature.properties.glyph
            ? feature.properties.glyph
            : markerTypeIcon;
          console.log('Map createClusterIcon - creating marker with color:', feature.properties.color, 'for event:', feature.properties.eventId);
          const marker = L.marker(latlng, {
            icon: L.icon.glyph.mdi({
              glyph: icon || "circle-medium",
              glyphColor: feature.properties.iconColor || "white",
              markerColor: feature.properties.color || "blue"
            })
          });
          this.bindFeatureNameLabel(marker, this.getFeatureNameLabel(feature));
          return marker;
        }
      }

      let count = feature.properties.point_count;
      let size = count < 5 ? "small" : count < 30 ? "medium" : "large";
      let icon = L.divIcon({
        html:
          "<div><span>" +
          feature.properties.point_count_abbreviated +
          "</span></div>",
        className: "marker-cluster marker-cluster-" + size,
        iconSize: L.point(40, 40)
      });

      return L.marker(latlng, {
        icon: icon
      });
    },
    clusterClickHandler(e) {
      this.endMarkerEdit();

      let clusterId = e.layer.feature.properties.cluster_id;
      let center = e.latlng;
      let expansionZoom;
      if (clusterId) {
        expansionZoom = this.superclusterIndex.getClusterExpansionZoom(
          clusterId
        );
        this.map.flyTo(center, expansionZoom);
      } else {
        let id = e.layer
          ? e.layer.feature
            ? e.layer.feature.properties
              ? e.layer.feature.properties.id
              : null
            : null
          : null;

        let editable = e.layer
          ? e.layer.feature
            ? e.layer.feature.properties
              ? e.layer.feature.properties.editable
              : null
            : null
          : null;

        if (editable) {
          let popupContent = this.createMarkerEditablePopup(id);
          var popup = L.popup()
            .setLatLng(center)
            .setContent(popupContent)
            .openOn(this.map);
        } else {
          let popupContent = this.createMarkerPopup(id);
          if (popupContent) {
            L.popup()
              .setLatLng(center)
              .setContent(popupContent)
              .openOn(this.map);
          }
        }

        // e.layer.bindPopup(popup);
        // e.layer.openPopup();
      }
    },
    createMarkerEditablePopup(id) {
      let markerData = this.markers.find(item => {
        return item.properties.id == id;
      });
      if (!markerData) return;
      let {
        color,
        markerType,
        description,
        title,
        eventId,
        eventTitle: eventTitleProp
      } = markerData.properties;
      let iconOptions = "";
      for (let mapMarkerType of this.mapMarkerTypesList) {
        iconOptions += `<option value="${mapMarkerType._id}" ${
          markerType === mapMarkerType._id ? "selected" : ""
        }>${mapMarkerType.title}</option>`;
      }
      let eventHeading = "";
      if (this.isAllEventsMode && eventId) {
        const eventTitle =
          (eventTitleProp || "").trim() || this.getEventTitleById(eventId);
        if (eventTitle) {
          eventHeading = `<p class="map-marker-event-heading"><strong>${this.escapeHtml(
            eventTitle
          )}</strong></p>`;
        }
      }
      return `
        <div style="direction: rtl; text-align: right;" class="map-popup">
          <div>
            ${eventHeading}
            <div class="popup-inputs">
              <input type="text" placeholder="כותרת הנקודה" id="layer-title-${id}" value="${title ||
        ""}" />
              <textarea class="textarea" rows="3" placeholder="תיאור הנקודה" id="layer-description-${id}">${description ||
        ""}</textarea>
              <div class="select">
                <select id="marker-icon-select-${id}">
                  ${iconOptions}
                </select>
              </div>
            </div>
            <div class="marker-buttons">
              <button class="v-btn v-btn--small theme--dark v-btn--depressed blue" onClick="saveMarkerData('${id}')">שמירה</button>
              <button class="v-btn v-btn--small theme--dark v-btn--depressed grey" onClick="startMarkerEdit('${id}')">הזזה</button>
              <button class="v-btn v-btn--small theme--dark v-btn--depressed error" onClick="deleteMarker('${id}')">מחיקה</button>
            </div>
          </div>
        </div>`;
    },
    createMarkerPopup(id) {
      let markerData = this.markers.find(item => {
        return item.properties.id == id;
      });
      if (!markerData) return;
      let { description, title, parentData, eventId, eventTitle: eventTitleProp } =
        markerData.properties;
      let html = `<div style="direction: rtl; text-align: right;" class="map-popup"><div>`;
      if (parentData) {
        html += `<h3><a href="${parentData.url}" target="_blank">${
          parentData.title
        }</a></h3>`;
      }
      if (this.isAllEventsMode && eventId) {
        const eventTitle =
          (eventTitleProp || "").trim() || this.getEventTitleById(eventId);
        if (eventTitle) {
          html += `<h3>${this.escapeHtml(eventTitle)}</h3>`;
        }
      }
      if (title) {
        if (!parentData || parentData.title !== title) {
          html += `<h3>${this.escapeHtml(title)}</h3>`;
        }
      } else if (this.isAllEventsMode && eventId) {
        const eventTitle = this.getEventTitleById(eventId);
        if (!eventTitle) {
          html += `<h3>${this.escapeHtml(String(eventId))}</h3>`;
        }
      }
      html += `<p>${description || ""}</p>`;
      html += `</div></div>`;
      return html;
    },
    markerIconChanged(id) {
      // let icon = document.getElementById(`marker-icon-select-${id}`).value;
      // let markerData = this.markers.find(item => {
      //   return item.properties.id == id;
      // });
      // if (markerData) {
      //   this.$set(markerData.properties, "icon", icon);
      // }
    },
    markerDescriptionChanged(id) {
      // let description = document.getElementById(`layer-description-${id}`)
      //   .value;
      // let markerData = this.markers.find(item => {
      //   return item.properties.id == id;
      // });
      // if (markerData) {
      //   this.$set(markerData.properties, "description", description);
      // }
    },
    saveMarkerData(id) {
      return new Promise((resolve, reject) => {
        let markerData = this.markers.find(item => {
          return item.properties.id == id;
        });
        let markerType = document.getElementById(`marker-icon-select-${id}`)
          .value;
        let title = document.getElementById(`layer-title-${id}`).value;
        let description = document.getElementById(`layer-description-${id}`)
          .value;
        let oldProperties = markerData.properties;
        this.$set(markerData.properties, "markerType", markerType);
        this.$set(markerData.properties, "description", description);
        this.$set(markerData.properties, "title", title);
        this.updateMarkerOnServer(markerData)
          .then(() => {
            this.map.closePopup();
            resolve();
          })
          .catch(err => {
            this.$set(markerData, "properties", oldProperties);
            console.log(err);
            reject(err);
          });
      });
    },
    startMarkerEdit(id) {
      this.saveMarkerData(id).then(() => {
        let markerData = this.markers.find(item => {
          return item.properties.id == id;
        });
        this.markerOnEdit = markerData;
        let coordinates = markerData.geometry.coordinates;
        let latlng = L.latLng(coordinates[1], coordinates[0]);
        let marker = L.marker(latlng, {
          icon: L.icon.glyph.mdi({
            glyph: "plus",
            glyphColor: "red",
            markerColor: "rgba(255, 255, 255, 0.2)"
          })
        });
        this.editableLayerGroup.addLayer(marker);
        this.status = statuses.editingMarker;
        this.myCustomEditHandler.enable();
        this.removeMarkerFromMap(id);
      });
    },
    removeMarkerFromMap(id) {
      this.markers = this.markers.filter(item => {
        return item.properties.id !== id;
      });
      this.map.closePopup();
    },
    endMarkerEdit() {
      this.myCustomEditHandler.disable();
      let latlng;
      this.editableLayerGroup.eachLayer(layer => {
        latlng = layer.getLatLng();
        this.editableLayerGroup.removeLayer(layer);
      });
      if (latlng) {
        let oldCoordinates = this.markerOnEdit.geometry.coordinates;
        this.markerOnEdit.geometry.coordinates = [latlng.lng, latlng.lat];
        this.updateMarkerOnServer(this.markerOnEdit)
          .catch(err => {
            this.markerOnEdit.geometry.coordinates = oldCoordinates;
            console.log(err);
          })
          .finally(() => {
            // this.markers.push(this.markerOnEdit);
            this.markerOnEdit = null;
            this.status = null;
          });
      }
    },
    updateUrlQuery() {
      if (this.map) {
        let zoom = this.map.getZoom();
        let center = this.map.getCenter().wrap();
        this.$router.replace({
          query: { lat: center.lat, lng: center.lng, z: zoom }
        });
      }
    },
    deleteMarker(id) {
      this.deleteMarkerOnServer(id)
        .then(() => {
          // this.removeMarkerFromMap(id);
          this.map.closePopup();
        })
        .catch(err => {
          console.log(err);
        });
    },
    deleteMarkerOnServer(id) {
      return new Promise((resolve, reject) => {
        let dataSource = this.dataSources[this.editableDataSourceId];
        axiosInstance({
          method: dataSource.remove.method,
          url: dataSource.remove.url + "/" + id
        })
          .then(({ data }) => {
            resolve(data);
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    isDrawableGeometryType(geometryType) {
      return (
        geometryType === "LineString" || geometryType === "Polygon"
      );
    },
    getDrawnShapeById(id) {
      return this.drawnShapes.find(item => item.properties.id == id);
    },
    findShapeLeafletLayer(id) {
      let found = null;
      if (!this.drawnShapesLayer) return null;
      this.drawnShapesLayer.eachLayer(layer => {
        if (layer._mapFeatureId == id) found = layer;
      });
      return found;
    },
    formatDistanceMeters(meters) {
      if (!meters || meters < 0) return "0 מ'";
      if (meters >= 1000) {
        return `${(meters / 1000).toFixed(2)} ק"מ`;
      }
      return `${Math.round(meters)} מ'`;
    },
    computeFeatureDistanceMeters(feature) {
      if (!feature || !feature.geometry) return 0;
      const g = feature.geometry;
      let coords = [];
      if (g.type === "LineString") {
        coords = g.coordinates;
      } else if (g.type === "Polygon" && g.coordinates[0]) {
        coords = g.coordinates[0];
      }
      if (coords.length < 2) return 0;
      let total = 0;
      for (let i = 1; i < coords.length; i++) {
        const a = L.latLng(coords[i - 1][1], coords[i - 1][0]);
        const b = L.latLng(coords[i][1], coords[i][0]);
        total += a.distanceTo(b);
      }
      if (g.type === "Polygon" && coords.length > 2) {
        const first = L.latLng(coords[0][1], coords[0][0]);
        const last = L.latLng(
          coords[coords.length - 1][1],
          coords[coords.length - 1][0]
        );
        total += last.distanceTo(first);
      }
      return total;
    },
    computePolygonAreaMeters(coords) {
      // Spherical excess formula for geodesic polygon area
      const R = 6371000;
      let area = 0;
      const n = coords.length;
      for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        const lat1 = (coords[i][1] * Math.PI) / 180;
        const lat2 = (coords[j][1] * Math.PI) / 180;
        const dLng = ((coords[j][0] - coords[i][0]) * Math.PI) / 180;
        area += dLng * (2 + Math.sin(lat1) + Math.sin(lat2));
      }
      return Math.abs((area * R * R) / 2);
    },
    formatAreaMeters(sqMeters) {
      if (!sqMeters || sqMeters < 0) return "0 מ²";
      if (sqMeters >= 1000000) {
        return `${(sqMeters / 1000000).toFixed(2)} קמ"ר`;
      }
      if (sqMeters >= 10000) {
        return `${(sqMeters / 10000).toFixed(2)} דונם`;
      }
      return `${Math.round(sqMeters)} מ²`;
    },
    getShapeDistanceLabel(feature) {
      if (feature.geometry.type === "Polygon") {
        const coords = feature.geometry.coordinates[0];
        if (coords && coords.length > 2) {
          const sqMeters = this.computePolygonAreaMeters(coords);
          return `שטח: ${this.formatAreaMeters(sqMeters)}`;
        }
        return "שטח: 0 מ²";
      }
      const meters = this.computeFeatureDistanceMeters(feature);
      return `אורך: ${this.formatDistanceMeters(meters)}`;
    },
    getFeatureNameLabel(feature) {
      const p = (feature && feature.properties) || {};
      const pointTitle = (p.title || p.name || "").trim();
      const eventTitle =
        (p.eventTitle || "").trim() || this.getEventTitleById(p.eventId);
      if (this.isAllEventsMode) {
        if (pointTitle && eventTitle) {
          return `${pointTitle} · ${eventTitle}`;
        }
        return eventTitle || pointTitle;
      }
      return pointTitle;
    },
    applyShapeStyleToLayer(layer, properties) {
      if (!layer || !layer.setStyle) return;
      const color = (properties && properties.color) || "#1976d2";
      layer.setStyle({
        color,
        weight: (properties && properties.weight) || 4,
        opacity: 1,
        fillColor: (properties && properties.fillColor) || color,
        fillOpacity:
          properties && properties.fillOpacity != null
            ? Number(properties.fillOpacity)
            : 0.2
      });
    },
    updateShapeDistanceLabel(layer, feature) {
      if (!layer || !feature) return;
      if (layer.unbindTooltip) layer.unbindTooltip();
      if (layer.unbindPopup) layer.unbindPopup();
      this.bindFeatureNameLabel(layer, this.getFeatureNameLabel(feature));
    },
    bindDrawnShapeLayerEvents(layer, id) {
      layer._mapFeatureId = id;
      layer.on("click", e => {
        L.DomEvent.stopPropagation(e);
        this.openDrawnShapePopup(id, e.latlng);
      });
    },
    createDrawnShapeEditablePopup(id) {
      const shape = this.getDrawnShapeById(id);
      if (!shape) return "";
      const p = shape.properties || {};
      const color = p.color || "#1976d2";
      const title = p.title || "";
      const description = p.description || "";
      const distanceLine = this.getShapeDistanceLabel(shape);
      return `
        <div style="direction: rtl; text-align: right;" class="map-popup drawn-shape-popup">
          <div class="popup-inputs">
            <input type="text" placeholder="כותרת" id="shape-title-${id}" value="${this.escapeHtml(
        title
      )}" />
            <textarea class="textarea" rows="3" placeholder="תיאור" id="shape-description-${id}">${this.escapeHtml(
        description
      )}</textarea>
            <label class="shape-color-label">צבע</label>
            <input type="color" id="shape-color-${id}" value="${color}" onchange="drawnShapeColorChanged('${id}')" />
            <p class="shape-distance-readout"><strong>${distanceLine}</strong></p>
          </div>
          <div class="marker-buttons">
            <button class="v-btn v-btn--small theme--dark v-btn--depressed blue" onClick="saveDrawnShapeData('${id}')">שמירה</button>
            <button class="v-btn v-btn--small theme--dark v-btn--depressed error" onClick="deleteDrawnShape('${id}')">מחיקה</button>
          </div>
        </div>`;
    },
    escapeHtml(str) {
      return String(str || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    openDrawnShapePopup(id, latlng) {
      const shape = this.getDrawnShapeById(id);
      if (!shape) return;
      const layer = this.findShapeLeafletLayer(id);
      const center =
        latlng ||
        (layer && layer.getBounds && layer.getBounds().getCenter()) ||
        this.map.getCenter();
      const popupContent = this.createDrawnShapeEditablePopup(id);
      L.popup()
        .setLatLng(center)
        .setContent(popupContent)
        .openOn(this.map);
    },
    drawnShapeColorChanged(id) {
      const el = document.getElementById(`shape-color-${id}`);
      if (!el) return;
      const color = el.value;
      const shape = this.getDrawnShapeById(id);
      if (!shape) return;
      this.$set(shape.properties, "color", color);
      this.$set(shape.properties, "fillColor", color);
      const layer = this.findShapeLeafletLayer(id);
      if (layer) this.applyShapeStyleToLayer(layer, shape.properties);
    },
    saveDrawnShapeData(id) {
      const shape = this.getDrawnShapeById(id);
      if (!shape) return Promise.resolve();
      const titleEl = document.getElementById(`shape-title-${id}`);
      const descEl = document.getElementById(`shape-description-${id}`);
      const colorEl = document.getElementById(`shape-color-${id}`);
      const oldProperties = Object.assign({}, shape.properties);
      if (titleEl) this.$set(shape.properties, "title", titleEl.value);
      if (descEl) this.$set(shape.properties, "description", descEl.value);
      if (colorEl) {
        this.$set(shape.properties, "color", colorEl.value);
        this.$set(shape.properties, "fillColor", colorEl.value);
      }
      const layer = this.findShapeLeafletLayer(id);
      if (layer) {
        this.applyShapeStyleToLayer(layer, shape.properties);
        this.updateShapeDistanceLabel(layer, shape);
      }
      shape.meta = shape.meta || {
        dataSourceId: this.editableDataSourceId
      };
      return this.updateMarkerOnServer(shape)
        .then(() => {
          this.map.closePopup();
        })
        .catch(err => {
          this.$set(shape, "properties", oldProperties);
          console.log(err);
          this.$store.dispatch("SHOW_SNACKBAR", {
            text: "שמירת הצורה נכשלה",
            color: "error"
          });
        });
    },
    savePolylineData(id) {
      return this.saveDrawnShapeData(id);
    },
    polylineColorChanged(id) {
      this.drawnShapeColorChanged(id);
    },
    deleteDrawnShape(id) {
      return this.deleteDrawnLayerOnServer(id).then(() => {
        this.map.closePopup();
      });
    },
    bringMarkersToFront() {
      if (this.clustersLayer && this.clustersLayer.bringToFront) {
        this.clustersLayer.bringToFront();
      }
    },
    drawnFeatureStyle(feature) {
      const p = (feature && feature.properties) || {};
      const color = p.color || "#1976d2";
      return {
        color,
        weight: p.weight || 4,
        opacity: 1,
        fillColor: p.fillColor || color,
        fillOpacity:
          p.fillOpacity != null ? Number(p.fillOpacity) : 0.2
      };
    },
    clearDrawnLayers() {
      if (!this.drawnShapesLayer) return;
      this.drawnShapesLayer.eachLayer(layer => {
        this.drawnShapesLayer.removeLayer(layer);
      });
    },
    addDrawnFeatureLayer(feature) {
      if (!this.drawnShapesLayer || !this.map || !feature) return;
      const id = feature.properties.id;
      const layerGroup = L.geoJSON(feature, {
        pane: "drawnShapesPane",
        style: f => this.drawnFeatureStyle(f),
        onEachFeature: (f, layer) => {
          layer._mapFeatureId = f.properties.id;
          layer._drawType =
            f.properties.drawType ||
            (f.geometry.type === "Polygon" ? "polygon" : "polyline");
          this.applyShapeStyleToLayer(layer, f.properties);
          this.updateShapeDistanceLabel(layer, f);
          this.bindDrawnShapeLayerEvents(layer, f.properties.id);
        }
      });
      layerGroup.eachLayer(layer => {
        this.drawnShapesLayer.addLayer(layer);
      });
    },
    renderDrawnLayers() {
      if (!this.drawnShapesLayer) return;
      this.clearDrawnLayers();
      for (const feature of this.drawnShapes) {
        this.addDrawnFeatureLayer(feature);
      }
      this.bringMarkersToFront();
    },
    layerToMapFeature(layer, layerType) {
      const raw = layer.toGeoJSON();
      const geometry =
        raw.type === "Feature" ? raw.geometry : raw;
      const dataSourceId = this.editableDataSourceId;
      const defaults =
        (this.dataSources[dataSourceId] &&
          this.dataSources[dataSourceId].defaultProperties) ||
        {};
      const existingId = layer._mapFeatureId;
      const existing = existingId ? this.getDrawnShapeById(existingId) : null;
      const existingProps = (existing && existing.properties) || {};
      const feature = {
        type: "Feature",
        geometry,
        properties: {
          id: existingId || existingProps.id || nanoid(),
          title: existingProps.title || "",
          description: existingProps.description || "",
          color:
            layer.options.color ||
            existingProps.color ||
            defaults.color ||
            "#1976d2",
          weight: layer.options.weight || existingProps.weight || 4,
          fillColor:
            layer.options.fillColor ||
            layer.options.color ||
            existingProps.fillColor ||
            defaults.color ||
            "#1976d2",
          fillOpacity:
            layer.options.fillOpacity != null
              ? layer.options.fillOpacity
              : existingProps.fillOpacity != null
                ? existingProps.fillOpacity
                : 0.2,
          drawType: layerType || existingProps.drawType,
          editable: true
        },
        meta: {
          dataSourceId
        }
      };
      if (this.isAllEventsMode) {
        const eventIdsToShow = this.$store.getters["events/eventIdsToShow"];
        if (eventIdsToShow && eventIdsToShow.length) {
          feature.properties.eventId = eventIdsToShow[0];
        }
      }
      return feature;
    },
    saveDrawnLayer(layer, layerType) {
      const dataSourceId = this.editableDataSourceId;
      if (dataSourceId < 0 || !this.dataSources[dataSourceId]) {
        this.$store.dispatch("SHOW_SNACKBAR", {
          text: "אין שכבת עריכה פעילה לשמירת הציור",
          color: "warning"
        });
        return;
      }
      const feature = this.layerToMapFeature(layer, layerType);
      layer._drawType = layerType;
      if (this.drawnShapesLayer && !this.drawnShapesLayer.hasLayer(layer)) {
        this.drawnShapesLayer.addLayer(layer);
      }
      this.createMarkerOnServer(feature)
        .then(response => {
          if (this.isAllEventsMode) {
            this.fetchData();
            return;
          }
          const id =
            (response && response._id) ||
            (response && response.feature && response.feature.properties.id) ||
            feature.properties.id;
          layer._mapFeatureId = id;
          feature.properties.id = id;
          const existing = this.drawnShapes.find(
            item => item.properties.id === id
          );
          if (!existing) {
            this.drawnShapes.push(feature);
          }
          if (this.drawnShapesLayer.hasLayer(layer)) {
            this.drawnShapesLayer.removeLayer(layer);
          }
          this.addDrawnFeatureLayer(feature);
          const savedLayer = this.findShapeLeafletLayer(id);
          this.bringMarkersToFront();
          if (savedLayer) {
            this.openDrawnShapePopup(id, savedLayer.getBounds().getCenter());
          }
        })
        .catch(err => {
          console.log("saveDrawnLayer error", err);
          if (this.drawnShapesLayer) {
            this.drawnShapesLayer.removeLayer(layer);
          }
          this.$store.dispatch("SHOW_SNACKBAR", {
            text: "שמירת הציור נכשלה",
            color: "error"
          });
        });
    },
    updateDrawnLayerOnServer(layer) {
      const id = layer._mapFeatureId;
      if (!id) return Promise.resolve();
      const dataSourceId = this.editableDataSourceId;
      if (dataSourceId < 0) return Promise.resolve();
      const feature = this.layerToMapFeature(
        layer,
        layer._drawType || "polyline"
      );
      feature.properties.id = id;
      feature.meta = { dataSourceId };
      const idx = this.drawnShapes.findIndex(
        item => item.properties.id === id
      );
      if (idx !== -1) {
        this.$set(this.drawnShapes, idx, feature);
        this.updateShapeDistanceLabel(layer, feature);
        this.applyShapeStyleToLayer(layer, feature.properties);
      }
      return this.updateMarkerOnServer(feature);
    },
    deleteDrawnLayerOnServer(id) {
      return this.deleteMarkerOnServer(id).then(() => {
        this.drawnShapes = this.drawnShapes.filter(
          item => item.properties.id !== id
        );
        if (this.drawnShapesLayer) {
          this.drawnShapesLayer.eachLayer(layer => {
            if (layer._mapFeatureId === id) {
              this.drawnShapesLayer.removeLayer(layer);
            }
          });
        }
        this.bringMarkersToFront();
      });
    },
    onDrawLayersEdited(e) {
      const layers = e.layers;
      const updates = [];
      layers.eachLayer(layer => {
        if (layer._mapFeatureId) {
          updates.push(this.updateDrawnLayerOnServer(layer));
        }
      });
      this.bringMarkersToFront();
      return Promise.all(updates).catch(err => {
        console.log("onDrawLayersEdited error", err);
        this.$store.dispatch("SHOW_SNACKBAR", {
          text: "עדכון הציור נכשל",
          color: "error"
        });
      });
    },
    onDrawLayersDeleted(e) {
      const layers = e.layers;
      const removals = [];
      layers.eachLayer(layer => {
        if (layer._mapFeatureId) {
          removals.push(
            this.deleteDrawnLayerOnServer(layer._mapFeatureId)
          );
        } else if (this.drawnShapesLayer) {
          this.drawnShapesLayer.removeLayer(layer);
        }
      });
      this.bringMarkersToFront();
      return Promise.all(removals).catch(err => {
        console.log("onDrawLayersDeleted error", err);
        this.$store.dispatch("SHOW_SNACKBAR", {
          text: "מחיקת הציור נכשלה",
          color: "error"
        });
      });
    },
    createMarkerOnServer(markerData) {
      return new Promise((resolve, reject) => {
        let dataSource = this.dataSources[markerData.meta.dataSourceId];
        
        // Use the featureToObject function from the data source if it exists
        let data;
        if (dataSource.create && dataSource.create.featureToObject) {
          data = dataSource.create.featureToObject(Object.assign({}, markerData));
          console.log('Map createMarkerOnServer - using dataSource featureToObject, data:', data);
        } else {
          // Fallback to direct data creation
          // Get current event ID from route or store
          const currentEventId = this.$route.params.id;
          data = {
            event: markerData.meta.eventId || currentEventId,
            feature: markerData,
            _id: markerData.properties.id
          };
          console.log('Map createMarkerOnServer - using fallback data creation, data:', data);
        }

        console.log('Map createMarkerOnServer - final data:', data);

        // Check if the data indicates כולם mode
        if (data.isAllEventsMode && Array.isArray(data.event)) {
          // In כולם mode, create the marker for all events in the ARAN
          console.log('Map createMarkerOnServer - כולם mode detected, adding marker to all events:', data.event);
          
          // Create promises for all events
          const promises = data.event.map(eventId => {
            let eventData = {
              event: eventId,
              feature: data.feature
            };
            console.log('Map createMarkerOnServer - creating for eventId:', eventId, 'with data:', eventData);
            
            return axiosInstance({
              method: dataSource.create.method,
              url: dataSource.create.url,
              data: eventData
            });
          });
          
          // Execute all promises
          Promise.all(promises)
            .then((responses) => {
              console.log('Map createMarkerOnServer - created markers for all events:', responses);
              // Return the first response for immediate display
              resolve(responses[0].data);
            })
            .catch(err => {
              console.log('Map createMarkerOnServer - error creating markers for all events:', err);
              reject(err);
            });
        } else {
          // Single event mode - create for current event only
          console.log('Map createMarkerOnServer - single event mode, creating for current event');
          axiosInstance({
            method: dataSource.create.method,
            url: dataSource.create.url,
            data
          })
            .then(({ data }) => {
              console.log('Map createMarkerOnServer - single event marker created:', data);
              resolve(data);
            })
            .catch(err => {
              console.log('Map createMarkerOnServer - error creating single event marker:', err);
              reject(err);
            });
        }
      });
    },
    updateMarkerOnServer(markerData) {
      return new Promise((resolve, reject) => {
        let dataSource = this.dataSources[markerData.meta.dataSourceId];
        let featureToObject = dataSource.update.featureToObject;
        let data = featureToObject(Object.assign({}, markerData));

        axiosInstance({
          method: dataSource.update.method,
          url: dataSource.update.url + "/" + data._id,
          data
        })
          .then(({ data }) => {
            resolve(data);
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    onResourceCreated(payload) {
      let parentId = payload.resource.partner || payload.resource.event;
      if (parentId && parentId._id) {
        parentId = parentId._id;
      }
      const allEventsParent =
        this.isAllEventsMode && parentId === "all-events";
      let i = 0;
      for (let dataSource of this.dataSources) {
        const matchesParent =
          dataSource.parentId === parentId ||
          (allEventsParent && dataSource.parentId === "all-events");
        if (matchesParent) {
          let feature = payload.resource.feature;
          feature.properties.id = payload.resource._id;
          feature.meta = { dataSourceId: i };
          if (feature.geometry && feature.geometry.type === "Point") {
            this.markers.push(feature);
          } else if (
            feature.geometry &&
            this.isDrawableGeometryType(feature.geometry.type)
          ) {
            const exists = this.drawnShapes.some(
              item => item.properties.id === feature.properties.id
            );
            if (!exists) {
              this.drawnShapes.push(feature);
              this.addDrawnFeatureLayer(feature);
            }
          }
        }
        ++i;
      }
    },
    onResourceUpdated(payload) {
      this.onResourceRemoved(payload);
      this.onResourceCreated(payload);
    },
    onResourceRemoved(payload) {
      const id = payload.resource._id;
      this.markers = this.markers.filter(item => {
        return item.properties.id !== id;
      });
      this.drawnShapes = this.drawnShapes.filter(item => {
        return item.properties.id !== id;
      });
      if (this.drawnShapesLayer) {
        this.drawnShapesLayer.eachLayer(layer => {
          if (layer._mapFeatureId === id) {
            this.drawnShapesLayer.removeLayer(layer);
          }
        });
      }
      this.bringMarkersToFront();
    },
    handleResize() {
      // set map to 0 so when resizing to smaller height the container height is
      // calculated correctly
      this.mapHeight = "0";
      setTimeout(() => {
        if (this.$refs.mapToolbar) {
          let mapContainer = this.$el;
          let mapToolbar = this.$refs.mapToolbar;
          // let sidebar = this.$refs.sidebar;

          let mapContainerHeight = parseInt(
            window.getComputedStyle(mapContainer, null).height
          );
          let mapToolbarHeight = parseInt(
            window.getComputedStyle(mapToolbar.$el, null).height
          );

          this.mapHeight = mapContainerHeight - mapToolbarHeight;
          setTimeout(() => {
            this.map.invalidateSize();
            this.fitBounds(); // not working before invalidateSize
          }, 1);
        }
      }, 200); // 200 for the animations to end
    },
    toggleSidebar() {
      this.isSidebarVisible = !this.isSidebarVisible;
    },
    toggleFullscreen() {
      this.fullscreen = !this.fullscreen;
      this.handleResize();
    },
    toggleCrosshair(show) {
      let crosshair = document.getElementById("crosshair" + this.mapId);
      if (crosshair) {
        crosshair.style.display = show ? "unset" : "none";
      }

      let mapCenter = document.getElementById("map-center" + this.mapId);
      if (mapCenter) {
        mapCenter.style.display = show ? "unset" : "none";
      }
    },
    normalizeCalTopoFeatureCollection(fc) {
      if (!fc || !Array.isArray(fc.features)) {
        return { type: "FeatureCollection", features: [] };
      }
      const strip2D = (coords) => {
        if (!coords) return coords;
        if (typeof coords[0] === "number") {
          return coords.length > 2 ? coords.slice(0, 2) : coords;
        }
        return coords.map(strip2D);
      };
      const features = fc.features.map(f => {
        const g = f.geometry ? { ...f.geometry } : null;
        if (!g || !g.coordinates) return f;
        if (g.type === "Point") {
          g.coordinates = strip2D(g.coordinates);
        } else if (g.type === "LineString" || g.type === "MultiPoint") {
          g.coordinates = strip2D(g.coordinates);
        } else if (g.type === "Polygon" || g.type === "MultiLineString") {
          g.coordinates = strip2D(g.coordinates);
        } else if (g.type === "MultiPolygon") {
          g.coordinates = strip2D(g.coordinates);
        }
        return { ...f, geometry: g };
      });
      return { type: "FeatureCollection", features };
    },
    applyCalTopoMapObjectsResponse(res) {
      const data = res.data;
      if (data && data.status === "error") {
        const apiDetail =
          (typeof data.message === "string" && data.message) ||
          (typeof data.error === "string" && data.error) ||
          (data.error && JSON.stringify(data.error)) ||
          JSON.stringify(data);
        this.$store.dispatch("SHOW_SNACKBAR", {
          text: `CalTopo: ${String(apiDetail).slice(0, 400)}`,
          color: "warning"
        });
        return;
      }
      let fc = null;
      if (data && data.status === "ok" && data.result && data.result.state) {
        fc = data.result.state;
      } else if (data && data.type === "FeatureCollection") {
        fc = data;
      }
      if (!fc || !fc.features || !fc.features.length) {
        return;
      }
      fc = this.normalizeCalTopoFeatureCollection(fc);
      const objectsLayer = L.geoJSON(fc, {
        style: feature => {
          const p = feature.properties || {};
          const stroke =
            p.stroke || p["marker-color"] || p.color || "#1976d2";
          return {
            color: stroke,
            weight: parseInt(p["stroke-width"], 10) || 3,
            opacity: 0.9,
            fillColor: p.fill || p["marker-color"] || stroke,
            fillOpacity:
              p["fill-opacity"] != null ? Number(p["fill-opacity"]) : 0.25
          };
        },
        pointToLayer: (feature, latlng) => {
          const p = feature.properties || {};
          const fill = p["marker-color"] || "#e53935";
          return L.circleMarker(latlng, {
            radius: 8,
            fillColor: fill,
            color: "#333",
            weight: 1,
            opacity: 1,
            fillOpacity: 0.9
          });
        },
        onEachFeature: (feature, layer) => {
          const p = feature.properties || {};
          const title = p.title || p.name || "";
          const desc = p.description || p.comments || "";
          if (title || desc) {
            layer.bindPopup(
              `<div dir="rtl"><strong>${title}</strong><br/>${desc}</div>`
            );
          }
        }
      });
      this.mapGroupedLayerControl.addOverlay(
        objectsLayer,
        "אובייקטים מהמפה",
        "CalTopo"
      );
    },
    showCalTopoMapObjectsFetchError(err) {
      let detail = (err && err.message) || String(err);
      if (err && err.response) {
        const st = err.response.status;
        const d = err.response.data;
        if (typeof d === "string" && d.length) {
          detail = `${detail} (HTTP ${st}) — ${d.slice(0, 300)}`;
        } else if (d && typeof d === "object") {
          const m =
            d.message ||
            d.error ||
            d.statusText ||
            JSON.stringify(d);
          detail = `${detail} (HTTP ${st}) — ${String(m).slice(0, 300)}`;
        } else {
          detail = `${detail} (HTTP ${st})`;
        }
      }
      this.$store.dispatch("SHOW_SNACKBAR", {
        text: detail.slice(0, 500),
        color: "warning"
      });
    },
    loadCalTopoMapObjectsOverlay() {
      const mapId = process.env.CALTOPO_API_ID;
      if (!mapId || !String(mapId).trim().length) return;
      if (!this.map || !this.mapGroupedLayerControl) return;

      const proxyBase = process.env.CALTOPO_MAP_V1_BASE;
      const ts = 0;
      const useBackendProxy =
        proxyBase && String(proxyBase).trim().length > 0;
      const devServerCalTopoReq = () =>
        axios.get(
          `${CALTOPO_DEV_MAP_PATH_PREFIX}/v1/map/${mapId}/since/${ts}`
        );

      if (useBackendProxy) {
        const proxyUrl = `${String(proxyBase).replace(/\/$/, "")}/map/${mapId}/since/${ts}`;
        axiosInstance
          .get(proxyUrl)
          .then(res => this.applyCalTopoMapObjectsResponse(res))
          .catch(err => {
            const st = err.response && err.response.status;
            if (st === 404 || st === 405) {
              if (process.env.NODE_ENV === "development") {
                console.warn(
                  "CalTopo backend proxy returned",
                  st,
                  "— using dev-server proxy to caltopo.com."
                );
                return devServerCalTopoReq().then(res =>
                  this.applyCalTopoMapObjectsResponse(res)
                );
              }
            }
            return Promise.reject(err);
          })
          .catch(err => this.showCalTopoMapObjectsFetchError(err));
      } else if (process.env.NODE_ENV === "development") {
        devServerCalTopoReq()
          .then(res => this.applyCalTopoMapObjectsResponse(res))
          .catch(err => this.showCalTopoMapObjectsFetchError(err));
      } else {
        console.warn(
          "CalTopo map objects overlay skipped in production: set CALTOPO_MAP_V1_BASE to a same-origin backend that proxies GET …/api/v1/map/<id>/since/<ts> (caltopo.com does not allow browser CORS)."
        );
      }
    },
    addMarker({ lat, lng }) {
      let dataSourceId = this.editableDataSourceId;
      let { markerType, iconColor, color } = this.dataSources[
        dataSourceId
      ].defaultProperties;
      
      // In כולם mode, we need to assign colors to each event
      // For new markers, we'll use the first event's color as default
      let markerColor = color;
      if (this.isAllEventsMode) {
        const eventIdsToShow = this.$store.getters["events/eventIdsToShow"];
        if (eventIdsToShow && eventIdsToShow.length > 0) {
          markerColor = this.getEventColor(eventIdsToShow[0]);
        }
      }
      
      let markerData = {
        type: "Feature",
        properties: {
          id: nanoid(),
          markerType,
          color: markerColor,
          iconColor,
          editable: true
        },
        meta: {
          dataSourceId
        },
        geometry: {
          type: "Point",
          coordinates: [lng, lat]
        }
      };
      
      console.log('Map addMarker - creating marker at:', lat, lng);
      console.log('Map addMarker - markerData:', markerData);
      
      this.createMarkerOnServer(markerData)
        .then((response) => {
          console.log('Map addMarker - marker created successfully:', response);
          
          // Check if we're in כולם mode
          const eventIdsToShow = this.$store.getters["events/eventIdsToShow"];
          const isAllEventsMode = eventIdsToShow && eventIdsToShow.length > 1;
          
          if (isAllEventsMode) {
            console.log('Map addMarker - כולם mode, refreshing data for all events');
            // In כולם mode, clear markers and refresh data for all events
            this.markers = [];
            this.fetchData();
            // Force a complete map refresh for כולם mode after a delay
            setTimeout(() => {
              console.log('Map addMarker - forcing map refresh for כולם mode');
              this.fetchData();
            }, 1000);
          } else {
            console.log('Map addMarker - single event mode, refreshing data');
            // Single event mode, clear markers and refresh data
            this.markers = [];
            this.fetchData();
            // Force a fresh fetch after a short delay to ensure new marker is loaded
            setTimeout(() => {
              console.log('Map addMarker - single event mode, forcing fresh fetch');
              this.fetchData();
            }, 500);
          }
        })
        .catch(err => {
          console.log('Map addMarker - error creating marker:', err);
        });
    }
  },

  sockets: {
    resource_created: function(payload) {
      if (payload.modelName === "MapFeature") {
        this.onResourceCreated(payload);
      }
    },
    resource_updated: function(payload) {
      if (payload.modelName === "MapFeature") {
        this.onResourceUpdated(payload);
      }
    },
    resource_removed: function(payload) {
      if (payload.modelName === "MapFeature") {
        this.onResourceRemoved(payload);
      }
    }
  },

  beforeMount() {
    require("../../Leaflet.Icon.Glyph.js");
    require("leaflet-groupedlayercontrol");
    // if (Array.isArray(this.dataSources)) {
    //   this.dataSources = this.dataSources.concat(this.dataSources);
    // }
  },

  mounted() {
    // Define glyph icons and factory
    L.Icon.Glyph.MDI = L.Icon.Glyph.extend({
      options: {
        prefix: "mdi",
        iconSize: [24, 24],
        iconAnchor: [12, 12],
        popupAnchor: [0, -12],
        glyphAnchor: [0, 0],
        glyphColor: "white",
        glyphSize: "18px"
      }
    });

    L.icon.glyph.mdi = function(options) {
      return new L.Icon.Glyph.MDI(options);
    };

    this.$nextTick(() => {
      // for maps outside of the event panel
      this.handleResize();
      window.addEventListener("resize", this.handleResize);

      let self = this;
      let center = [0, 0];
      let zoom = 4;
      let lat = this.$route.query.lat;
      let lng = this.$route.query.lng;
      let z = this.$route.query.z;
      if (lat && lng) {
        center = [lat, lng];
      }
      if (z) {
        zoom = z;
      }
      let map = L.map(this.mapId, {
        minZoom: 2
      }).setView(center, zoom);
      this.map = map;

      // Draw — shapes on a lower pane so markers stay clickable above them
      if (!map.getPane("drawnShapesPane")) {
        map.createPane("drawnShapesPane");
        map.getPane("drawnShapesPane").style.zIndex = 450;
      }
      this.drawnShapesLayer = new L.FeatureGroup();
      map.addLayer(this.drawnShapesLayer);
      this.editableLayerGroup = new L.FeatureGroup();
      map.addLayer(this.editableLayerGroup);

      L.drawLocal = {
        draw: {
          toolbar: {
            actions: {
              title: "מחיקת הנתיב",
              text: "ביטול"
            },
            finish: {
              title: "סיום נתיב",
              text: "סיום"
            },
            undo: {
              title: "מחיקת הנקודה האחרונה",
              text: "מחיקת הנקודה האחרונה"
            },
            buttons: {
              polyline: "יצירת נתיב", // Draw a polyline
              polygon: "יצירת פוליגון", // Draw a polygon
              rectangle: "", // Draw a rectangle
              circle: "", // Draw a circle
              marker: "יצירת נקודת עניין", // Draw a marker
              circlemarker: "" // Draw a circlemarker
            }
          },
          handlers: {
            circle: {
              tooltip: {
                start: "" // Click and drag to draw circle.
              },
              radius: "" // Radius
            },
            circlemarker: {
              tooltip: {
                start: "" // Click map to place circle marker.
              }
            },
            marker: {
              tooltip: {
                start: "לחיצה על המפה תמקם את נקודת העניין" // Click map to place marker.
              }
            },
            polygon: {
              tooltip: {
                start: "לחיצה להתחלת הצורה",
                cont: "לחיצה להמשך הצורה",
                end: "לחיצה על הנקודה הראשונה לסגירה"
              }
            },
            polyline: {
              error: "<strong>שגיאה:</strong> נתיב לא יכול לחתוך עצמו!", // <strong>Error:</strong> shape edges cannot cross!
              tooltip: {
                start: "להתחלת הנתיב יש ללחוץ על המפה", // Click to start drawing line.
                cont: "להמשך יצירת הנתיב יש ללחוץ על המפה", // Click to continue drawing line.
                end: "כדי לסיים עריכה, יש ללחוץ על הנקודה האחרונה" // Click last point to finish line.
              }
            },
            rectangle: {
              tooltip: {
                start: "" // Click and drag to draw rectangle.
              }
            },
            simpleshape: {
              tooltip: {
                end: "" // Release mouse to finish drawing.
              }
            }
          }
        },
        edit: {
          toolbar: {
            actions: {
              save: {
                title: "שמירת שינויים", // Save changes
                text: "שמירת שינויים" // Save
              },
              cancel: {
                title: "השינויים לא ישמרו", // Cancel editing, discards all changes
                text: "ביטול" // Cancel
              },
              clearAll: {
                title: "נקה הכל", // Clear all layers
                text: "מחק הכל" // Clear All
              }
            },
            buttons: {
              edit: "עריכת אלמנטים", // Edit layers
              editDisabled: "אין אלמנטים לערוך", // No layers to edit
              remove: "מחיקת אלמנטים", // Delete layers
              removeDisabled: "אין אלמנטים למחוק" // No layers to delete
            }
          },
          handlers: {
            edit: {
              tooltip: {
                text: "", // Drag handles or markers to edit features.
                subtext: "" // Click cancel to undo changes.
              }
            },
            remove: {
              tooltip: {
                text: "כדי למחוק אלמנט, יש ללחוץ עליו" // Click on a feature to remove.
              }
            }
          }
        }
      };

      this.myCustomEditHandler = new L.EditToolbar.Edit(this.map, {
        featureGroup: this.editableLayerGroup,
        remove: false
      });
      const drawnShapesLayerRef = this.drawnShapesLayer;

      var drawControl = new L.Control.Draw({
        position: "topleft",
        draw: {
          polyline: {
            shapeOptions: {
              color: "#1976d2",
              opacity: 1,
              weight: 4
            }
          },
          marker: {
            icon: L.icon.glyph.mdi({
              glyph: "plus",
              glyphColor: "black",
              markerColor: "rgba(255, 255, 255, 0.5)"
            })
          },
          polygon: {
            shapeOptions: {
              color: "#1976d2",
              weight: 3,
              fillColor: "#1976d2",
              fillOpacity: 0.2
            }
          },
          circle: false,
          rectangle: false,
          circlemarker: false
        },
        edit: {
          featureGroup: drawnShapesLayerRef,
          remove: true,
          edit: true
        }
      });

      if (this.drawEnabled) {
        map.addControl(drawControl);
      }

      let container = map.getContainer();
      let crosshair = L.DomUtil.create("div", "leaflet-crosshair", container);
      crosshair.innerHTML = `<div class="crosshair" id="crosshair${
        this.mapId
      }"></div>`;
      this.map.on("popupopen", e => {
        this.toggleCrosshair(false);
      });
      this.map.on("popupclose", e => {
        this.toggleCrosshair(true);
      });

      // exopse functions on window
      window.startMarkerEdit = index => {
        self.startMarkerEdit(index);
      };
      window.saveMarkerData = index => {
        self.saveMarkerData(index);
      };
      window.savePolylineData = index => {
        self.savePolylineData(index);
      };
      window.saveDrawnShapeData = id => {
        self.saveDrawnShapeData(id);
      };
      window.deleteDrawnShape = id => {
        self.deleteDrawnShape(id);
      };
      window.drawnShapeColorChanged = id => {
        self.drawnShapeColorChanged(id);
      };
      window.deleteMarker = index => {
        self.deleteMarker(index);
      };
      window.markerIconChanged = index => {
        self.markerIconChanged(index);
      };
      window.markerDescriptionChanged = index => {
        self.markerDescriptionChanged(index);
      };
      window.polylineColorChanged = index => {
        self.polylineColorChanged(index);
      };

      window.startMergingPolylines = index => {
        self.startMergingPolylines(index);
      };
      window.startCuttingPolyline = index => {
        self.startCuttingPolyline(index);
      };
      window.reversePolyline = index => {
        self.reversePolyline(index);
      };
      window.addMarker = ({ lat, lng }) => {
        self.addMarker({ lat, lng });
      };

      map.on(L.Draw.Event.CREATED, function(e) {
        let type = e.layerType,
          layer = e.layer;
        if (type === "marker") {
          let latlng = layer.getLatLng();
          self.addMarker({ lat: latlng.lat, lng: latlng.lng });
        } else if (type === "polyline" || type === "polygon") {
          self.saveDrawnLayer(layer, type);
        } else {
          self.editableLayerGroup.addLayer(layer);
        }
      });

      map.on(L.Draw.Event.EDITED, function(e) {
        self.onDrawLayersEdited(e);
      });

      map.on(L.Draw.Event.DELETED, function(e) {
        self.onDrawLayersDeleted(e);
      });

      this.fetchData();

      this.clustersLayer = L.geoJSON(null, {
        pointToLayer: this.createClusterIcon
      }).addTo(this.map);
      this.clustersLayer.on("click", this.clusterClickHandler);
      this.bringMarkersToFront();

      this.map.on("click", e => {
        ++self.clickLock;
        setTimeout(() => {
          if (self.clickLock) {
            if (self.status === statuses.cuttingPolyline) {
              // self.cutPolylineEnd();
            } else if (self.status === statuses.mergingPolylines) {
              // self.mergingPolylinesEnd();
            } else if (self.status === statuses.editingMarker) {
              self.endMarkerEdit();
            }
          }
        }, 200);
      });

      this.map.on("dblclick", e => {
        self.clickLock = 0;
      });

      this.map.on("zoomend", e => {
        this.updateUrlQuery();
      });

      this.map.on("moveend", e => {
        this.updateClusters();
        this.updateUrlQuery();
        this.centerLat = this.map.getCenter().wrap().lat;
        this.centerLng = this.map.getCenter().wrap().lng;
      });

      // Layers
      var baselayers = {
        "OSM טופוגרפית": L.tileLayer(
          "https://{s}.tile.thunderforest.com/landscape/{z}/{x}/{y}{r}.png?apikey=90af9dcedf0d4f389bfc4aed22fce55b",
          {
            maxZoom: 17,
            subdomains: ["a", "b", "c"],
            attribution:
              '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          }
        ),
        "OSM עירונית": L.tileLayer(
          "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
          {
            maxZoom: 19,
            attribution:
              '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          }
        ),
        "גוגל היברידי": L.tileLayer(
          "https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}",
          {
            maxZoom: 20,
            subdomains: ["mt0", "mt1", "mt2", "mt3"]
          }
        ),
        "גוגל טופוגרפית": L.tileLayer(
          "https://mt1.googleapis.com/vt?lyrs=t,r&x={x}&y={y}&z={z}&style=high_dpi",
          {
            maxZoom: 20,
            subdomains: ["mt0", "mt1", "mt2", "mt3"]
          }
        ),
        "גוגל לווין": L.tileLayer(
          "https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
          {
            maxZoom: 20,
            subdomains: ["mt0", "mt1", "mt2", "mt3"]
          }
        ),
        "ESRI לווין": L.tileLayer(
          "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
          {
            maxZoom: 20,
            subdomains: ["mt0", "mt1", "mt2", "mt3"]
          }
        ),
        "CalTopo MapBuilder": L.tileLayer(
          "https://caltopo.com/tile/mb_topo/{z}/{x}/{y}.png?ctdarkmode=false",
          {
            maxZoom: 20,
            attribution: '&copy; <a href="https://caltopo.com/">CalTopo</a>',
            tileSize: 256
          }
        ),
        "CalTopo Hybrid": L.tileLayer(
          "https://caltopo.com/tile/mb_hybrid/{z}/{x}/{y}.png?ctdarkmode=false",
          {
            maxZoom: 20,
            attribution: '&copy; <a href="https://caltopo.com/">CalTopo</a> Hybrid',
            tileSize: 256
          }
        ),
        // Global Imagery (satellite/aerial). Do not use mb_aerial — CalTopo serves empty tiles for that slug.
        "CalTopo Imagery": L.tileLayer(
          "https://caltopo.com/tile/imagery/{z}/{x}/{y}.png?ctdarkmode=false",
          {
            maxZoom: 20,
            attribution:
              '&copy; <a href="https://caltopo.com/">CalTopo</a> Global Imagery',
            tileSize: 256
          }
        ),
        "CalTopo Roads": L.tileLayer(
          "https://caltopo.com/tile/mbr/{z}/{x}/{y}.png?ctdarkmode=false",
          {
            maxZoom: 20,
            attribution: '&copy; <a href="https://caltopo.com/">CalTopo</a> Roads',
            tileSize: 256
          }
        )
      };

      const caltopoApiId = process.env.CALTOPO_API_ID;
      let caltopoContoursOverlay = null;
      if (caltopoApiId && String(caltopoApiId).trim().length > 0) {
        // No trailing ? — Leaflet appends ?SERVICE=… CalTopo returns empty tiles if param *names* are lowercase.
        const wmsBase = `https://caltopo.com/api/${caltopoApiId}/wms`;
        const wmsAttribution =
          '&copy; <a href="https://caltopo.com/">CalTopo</a> (API WMS)';
        caltopoContoursOverlay = L.tileLayer.wms(wmsBase, {
          layers: "c",
          format: "image/png",
          transparent: true,
          version: "1.3.0",
          uppercase: true,
          maxZoom: 20,
          attribution: wmsAttribution
        });
        CALTOPO_API_WMS_LAYERS.forEach(({ code, name }) => {
          baselayers[`CalTopo API · ${name}`] = L.tileLayer.wms(wmsBase, {
            layers: code,
            format: "image/png",
            transparent: true,
            version: "1.3.0",
            uppercase: true,
            maxZoom: 20,
            attribution: wmsAttribution
          });
        });
      }

      let t = {
        // "<b>מזג אוויר</b>": {
        //   עננות: L.tileLayer(
        //     "https://tile.openweathermap.org/map/clouds_new/{z}/{x}/{y}.png?appid=fe2fa688c628218d891cc9d176a1d86e",
        //     {}
        //   ),
        //   משקעים: L.tileLayer(
        //     "https://tile.openweathermap.org/map/precipitation_new/{z}/{x}/{y}.png?appid=fe2fa688c628218d891cc9d176a1d86e",
        //     {}
        //   ),
        //   רוחות: L.tileLayer(
        //     "https://tile.openweathermap.org/map/wind_new/{z}/{x}/{y}.png?appid=fe2fa688c628218d891cc9d176a1d86e",
        //     {}
        //   ),
        //   טמפרטורה: L.tileLayer(
        //     "https://tile.openweathermap.org/map/temp_new/{z}/{x}/{y}.png?appid=fe2fa688c628218d891cc9d176a1d86e",
        //     {}
        //   ),
        //   "מכ״ם": L.tileLayer(
        //     "https://tilecache.rainviewer.com/v2/radar/1557322200/512/{z}/{x}/{y}/1/1_1.png",
        //     {}
        //   )
        // }
      };
      if (caltopoContoursOverlay) {
        t.CalTopo = {
          "CalTopo API · Contours": caltopoContoursOverlay
        };
      }

      let groupedLayersOptions = {
        position: "bottomleft",
        exclusiveGroups: ["מפות בסיס"]
      };

      this.mapGroupedLayerControl = L.control
        .groupedLayers(baselayers, t, groupedLayersOptions)
        .addTo(this.map);
      baselayers["גוגל טופוגרפית"].addTo(this.map);

      this.loadCalTopoMapObjectsOverlay();

      this.$bus.$on("map-invalidate-size", () => {
        setTimeout(() => {
          this.map.invalidateSize();
          this.handleResize();
        }, 10);
      });

      setTimeout(() => {
        this.map.invalidateSize();
      }, 10);
    });
  },

  beforeDestroy: function() {
    window.removeEventListener("resize", this.handleResize);
  },

  watch: {
    markerLabelsPermanent() {
      this.updateClusters(true);
    },
    markers: {
      handler: function(val) {
        console.log('Map markers watcher - markers changed, count:', val.length);
        // Log the colors of all markers
        val.forEach((marker, index) => {
          console.log(`Map markers watcher - marker ${index}:`, {
            eventId: marker.properties.eventId,
            color: marker.properties.color,
            id: marker.properties.id
          });
        });
        
        this.superclusterIndex = new supercluster({
          radius: 0, // Disable clustering - show individual markers
          extent: 256,
          maxZoom: 12
        }).load(val);
        this.updateClusters(true);
      },
      deep: true
    },
    dataSources: {
      handler: function() {
        console.log('watch dataSources');
        this.fetchData();
      },
      deep: true
    }
  },

  components: {
    LTileLayer,
    LControlLayers,
    MapSidebar,
    MapToolbar,
    MapCenter
  }
};
</script>

<style scoped lang="scss">
@import "~leaflet/dist/leaflet.css";
@import "~leaflet-draw/dist/leaflet.draw.css";
// @import "~leaflet-fullscreen/dist/Leaflet.fullscreen.css";
@import "~leaflet-geosearch/assets/css/leaflet.css";

.fullscreen-map-container {
  position: fixed;
  z-index: 100;
  background-color: white;
  width: 100%;
  height: 100%;
  top: 0;
  right: 0;
}
</style>

<style lang="scss">
.map {
  width: 100%;
  height: 100%;
  direction: ltr;
}

.map-toolbar {
  .v-toolbar__content {
    padding: 0 0 !important;
  }
}

.leaflet-pane {
  z-index: 6 !important;
}

.leaflet-top,
.leaflet-bottom {
  z-index: 7 !important;
}

.leaflet-control-geosearch {
  z-index: 7 !important;
}

.leaflet-control-geosearch a.reset {
  right: unset;
  left: 0;
}

.leaflet-glyph-icon span {
  position: absolute;
  border-radius: 50%;
  width: 24px;
  height: 24px;
}

.marker-cluster-small {
  background-color: rgba(181, 226, 140, 0.6);
}
.marker-cluster-small div {
  background-color: rgba(110, 204, 57, 0.6);
}

.marker-cluster-medium {
  background-color: rgba(241, 211, 87, 0.6);
}
.marker-cluster-medium div {
  background-color: rgba(240, 194, 12, 0.6);
}

.marker-cluster-large {
  background-color: rgba(253, 156, 115, 0.6);
}
.marker-cluster-large div {
  background-color: rgba(241, 128, 23, 0.6);
}

.marker-cluster {
  background-clip: padding-box;
  border-radius: 20px;
}
.marker-cluster div {
  width: 30px;
  height: 30px;
  margin-left: 5px;
  margin-top: 5px;

  text-align: center;
  border-radius: 15px;
  font: 14px "Helvetica Neue", Arial, Helvetica, sans-serif;
}
.marker-cluster span {
  line-height: 30px;
}

.leaflet-draw-tooltip {
  display: none;
}

.drawn-shape-distance-label {
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #1976d2;
  border-radius: 4px;
  color: #1565c0;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 6px;
  pointer-events: none;
  white-space: nowrap;
  z-index: 1000;
}

.map-marker-event-heading {
  margin: 0 0 8px;
  font-size: 14px;
}

.drawn-shape-popup .shape-color-label {
  display: block;
  font-size: 13px;
  margin: 6px 0 4px;
}

.drawn-shape-popup input[type="color"] {
  width: 100%;
  height: 36px;
  padding: 2px;
  cursor: pointer;
}

.shape-distance-readout {
  font-size: 13px;
  margin: 8px 0 0;
}

.leaflet-popup-content {
  margin: 10px !important;
}

.map-popup {
  min-width: 250px !important;
}

.map-popup hr {
  margin: 10px 0 !important;
  border: 0.5px solid gray;
}

.leaflet-popup-close-button {
  display: none;
}

.leaflet-popup-content-wrapper {
  border-radius: 4px;
}

.leaflet-popup-content table {
  width: 100%;
}

.leaflet-popup-content table td,
.leaflet-popup-content table th {
  padding: 5px;
  border: 1px solid #eee;
  color: #999;
}

.leaflet-popup-close-button {
  right: unset !important;
  left: 0 !important;
  padding: 4px 0 0 4px !important;
}

.leaflet-control-attribution {
  display: none;
}

.popup-inputs {
  margin-bottom: 10px;
}

.popup-inputs input {
  width: 100%;
  margin-bottom: 7px;
  font-size: 1rem;
}

.popup-inputs .select {
  width: 100%;
  margin-top: 5px;
  font-size: 1rem;
}

.popup-inputs select,
.popup-inputs input,
.popup-inputs textarea {
  width: 100%;
}

.marker-buttons {
  text-align: center;
}

.marker-buttons button {
  min-width: 55px !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
  margin-top: 0 !important;
}

.map-popup {
  input[type="email"],
  input[type="number"],
  input[type="password"],
  input[type="search"],
  input[type="tel"],
  input[type="text"],
  input[type="url"],
  textarea,
  select {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-color: transparent;
    border: 0.1rem solid #d1d1d1;
    border-radius: 0.2rem;
    box-shadow: none;
    box-sizing: inherit;
    height: 2.8rem;
    padding: 0.6rem 1rem;
    width: 100%;
    font-size: 14px;
  }

  input[type="email"]:focus,
  input[type="number"]:focus,
  input[type="password"]:focus,
  input[type="search"]:focus,
  input[type="tel"]:focus,
  input[type="text"]:focus,
  input[type="url"]:focus,
  textarea:focus,
  select:focus {
    border-color: #777;
    outline: 0;
  }

  select {
    background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="10" viewBox="0 0 29 14" width="24"><path fill="%23d1d1d1" d="M9.37727 3.625l5.08154 6.93523L19.54036 3.625"/></svg>')
      center left no-repeat;
    padding-left: 3rem;
  }

  select:focus {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="10" viewBox="0 0 29 14" width="24"><path fill="%23777" d="M9.37727 3.625l5.08154 6.93523L19.54036 3.625"/></svg>');
  }

  textarea {
    min-height: 5rem;
  }

  label,
  legend {
    display: block;
    font-size: 1.6rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  fieldset {
    border-width: 0;
    padding: 0;
  }

  input[type="checkbox"],
  input[type="radio"] {
    display: inline;
  }

  .label-inline {
    display: inline-block;
    font-weight: normal;
    margin-left: 0.5rem;
  }
}

// Leaflet
.leaflet-control-geosearch {
  direction: rtl;

  a.reset {
    display: none !important;
  }
}

.leaflet-control-layers {
  direction: rtl;
}

.crosshair {
  width: 40px;
  height: 40px;
  background-image: url("/static/crosshair.svg");
  background-size: 40px;
}

.leaflet-crosshair {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
  z-index: 7;

  display: -webkit-box;
  display: -moz-box;
  display: -ms-flexbox;
  display: -webkit-flex;
  display: flex;
  -webkit-box-align: center;
  -webkit-flex-align: center;
  -ms-flex-align: center;
  -webkit-align-items: center;
  align-items: center;
}

.leaflet-crosshair > * {
  position: relative;
  z-index: 8;
  margin: auto;
}
</style>


// WEBPACK FOOTER //
// src/components/map/Map.vue